import { useState, type ReactNode } from 'react';
import { countedText } from './CountedLabel';
import { ChevronDown, ChevronUp, Eye, EyeOff, Ruler, Tag } from 'lucide-react';
import { SegmentedToggle } from './SegmentedToggle';
import { useLanguage } from '../../contexts/LanguageContext';

// WHAT IS IN THE PICTURE, LISTED — one card per part the viewer has loaded, down the side of
// the viewport it is about.
//
// The parts are already named twice on this page: in the two selectors that chose them, and
// in the chips of the sub-step they come from. Neither says what is ON SCREEN, which is a
// different list — a fitting draws the two parts it is about and nothing else — and neither
// can be clicked to take a part out of the way.
//
// THAT IS WHAT THESE ARE FOR. A washer seats inside a counterbore and a screw head covers the
// hole it went into: the thing you need to see is behind the thing you can see, and the only
// way to look at it is to take the other one out of the picture for a moment. Hiding is not a
// fact about the fitting and nothing here is stored — it is a way of looking, like turning
// the view, and it comes back the moment the sub-step does.
//
// A hidden part is also not clickable in the viewport, which follows from it not being there:
// what is not drawn cannot be picked, and the card is how it comes back.
// THE THREE PARTS A FITTING CAN DRAW, by the instance id the assembly gives them — the part
// everything is measured from, the part that travels, and the thing in the hand that turns
// it. See assemblyOf, which mints these; nothing here decides them.
const ROLES: Record<string, 'target' | 'moving' | 'tool' | 'prep' | 'next' | undefined> = {
  target: 'target',
  moving: 'moving',
  tool: 'tool',
  // And the second, for the second hardware (see Fitting.nextToolId).
  tool2: 'tool',
  // The hardware prepared on the moving part in the hand (see Fitting.prepOn).
  prep: 'prep',
  // And the second hardware put into the part fitted onto (see Fitting.nextOn).
  next: 'next',
  // And the hardware prepared on that one (see Fitting.nextPrep).
  nextPrep: 'prep',
  // And the third hardware, after the second (see Fitting.lastOn).
  last: 'next',
};

// And their colours: the same three, in the same order, that the wireframe draws each part
// in — see WIRE_COLOURS in assemblyScene. Teal, violet, amber.
const ROLE_TONE: Record<'target' | 'moving' | 'tool' | 'prep' | 'next', string> = {
  target: 'text-teal-700',
  moving: 'text-violet-600',
  tool: 'text-amber-700',
  prep: 'text-violet-400',
  next: 'text-violet-400',
};

// THE SWITCH THAT CLEARS THE PICTURE — see `marksOn` below. Its own component because it is
// drawn in two places: at the head of the cards, and in the row over the viewport where a
// caller moves the viewer's controls out of the picture (PartViewer's `controlsAbove`).
// A NAME MADE OF SEVERAL — a set's, "JCBC screw (E) + Spring washer (F) + …" — broken only between
// its parts, each "+" at the start of the line it begins. One part to a piece of the line, kept
// whole: broken inside, "Spring" ended a line and "washer (F) +" began the next.
//
// AND A PART IN BRACKETS — "(+ Barrel nut (G))", one made under Tools (see setName) — kept whole too.
function PlusLines({ label }: { label: string }) {
  const [own, ...made] = label.split(/ (?=\(\+ )/);
  const parts = [...own.split(' + ').map((p, i) => (i > 0 ? `+ ${p}` : p)), ...made];
  if (parts.length < 2) return <>{label}</>;
  return (
    <span>
      {parts.map((p, i) => (
        <span key={i}>
          {i > 0 && ' '}
          <span className="inline-block">{p}</span>
        </span>
      ))}
    </span>
  );
}

// Where a name stands in an order, those it does not hold after all of it.
const rankIn = (order: string[], name: string) => {
  const at = order.indexOf(name);
  return at < 0 ? order.length : at;
};

// TWO NAMED HALVES, not a ruler icon that was on or off (Louis 2026-10-28): which one is lit says
// what the picture is showing, without hovering to find out.
export function MarksButton({ on, onToggle, className = '', small = false }: {
  on: boolean;
  onToggle: () => void;
  className?: string;
  small?: boolean;
}) {
  const { t } = useLanguage();
  return (
    <SegmentedToggle
      value={on ? 'marks' : 'parts'}
      options={[
        { value: 'parts', label: t('notice.assembly.partsOnly') },
        { value: 'marks', label: t('notice.assembly.withMarks') },
      ]}
      onChange={(v) => { if ((v === 'marks') !== on) onToggle(); }}
      className={`shadow-sm ${className}`}
      small={small}
    />
  );
}

export function ScenePartCards({
  parts, hidden, onToggle, rulersOff, onToggleRuler,
  facesOff, onToggleFaces, picked, onPick, panel, marksOn, onToggleMarks,
  pieces, pieceLabel, pieceOrder, piecesOff, onTogglePiece, piecesBoxed, onBoxPiece, boxesOff, onToggleBox,
  measureGroups, onToggleMeasureGroup, inline = false, below = false,
}: {
  // `group`: a piece of hardware, or a cover — each listed apart, and merged with its likes.
  parts: { instanceId: string; label: string; group?: 'hardware' | 'cover' }[];
  hidden: string[];
  onToggle: (instanceId: string) => void;
  // WHOSE MEASURED BOX IS OFF, and the switch for it. A SETTING, and each card's own.
  //
  // It was a VOTE: one set of arrows measured everything on screen at once, so they all had
  // to be on for anything to be drawn and any one card turned them all off — the icon lit on
  // every card and dimming on every card, because "this one is off" and "they are all off"
  // could not be said with the same mark. Each part carries its own box now (see
  // assemblyScene), so the button means what it looks like it means.
  rulersOff: string[];
  onToggleRuler: (instanceId: string) => void;
  // WHOSE SIX FACES ARE NAMED, and the switch for it — the ruler's opposite number. The two
  // used to be one button because the words and the numbers hang off one box, which is how it
  // is drawn and not what is being asked: "how big is it" and "which way round is it" are
  // looked at at different moments, and each answer covers the other. The box is drawn while
  // either is on, so switching both off is how you get a clean look at the mesh.
  //
  // Optional, and the icon appears only where a caller offers it.
  facesOff?: string[];
  onToggleFaces?: (instanceId: string) => void;
  // WHICH ONE IS OPEN, and how to open another. The card is two controls in one rectangle
  // now: the eye takes the part out of the picture, the NAME selects it and unfolds its
  // numbers underneath. They were one button doing the first thing only, and the numbers
  // lived in the opposite corner of the viewport — so the part you were reading and the name
  // of the part you were reading were as far apart as the viewer is wide.
  picked: string | null;
  onPick: (instanceId: string | null) => void;
  // The picked part's panel, rendered by the caller and glued under its own card. Given here
  // rather than built here because it is the caller that holds the fitting those numbers are
  // written into; this file owns where it goes, not what is in it.
  panel?: ReactNode;
  // WHETHER ANYTHING IS DRAWN ABOUT THE PARTS AT ALL — the boxes, the six face names and the
  // gizmo — and the one press that puts the lot out or brings it back.
  //
  // A master switch and not a fourth per-part one, because the question it answers is about
  // the PICTURE rather than about a part: what a fitting finally has to be judged on is two
  // pieces of metal meeting, and eight labels, six arrows and three crosses are between you
  // and that. It is the same write the play button makes (see AssemblyPhase) — off is a
  // state the cards and the gizmo button then agree with, not a suppression they contradict.
  //
  // Optional, and the button appears only where a caller offers it.
  marksOn?: boolean;
  onToggleMarks?: () => void;
  // UNDER A ROW OF CONTROLS in the corner (the draw mode and the marks switch, see PartViewer),
  // rather than in the corner itself.
  below?: boolean;
  // ONE PART'S OWN MESHES, where its file holds several — a row each under the cards, each with
  // its own eye. A written model is one mesh per component, and this is where they are named.
  // Optional, and drawn only where a caller offers them.
  pieces?: string[];
  // How a mesh's row reads, where not by its name: a set's piece made under Tools, in brackets.
  pieceLabel?: (name: string) => string;
  // AND THE ORDER THEY ARE LISTED IN, where given — a set's, as its name lists them. Any it does not
  // name follow, as they came.
  pieceOrder?: string[];
  piecesOff?: string[];
  onTogglePiece?: (name: string) => void;
  // WHICH OF THEM WEAR A MEASURED BOX, and the switch for it — the row's NAME, as the name on a
  // part's card is what opens its numbers. The eye beside it keeps the one job of hiding it.
  piecesBoxed?: string[];
  onBoxPiece?: (name: string) => void;
  // WHOSE BOX IS OFF, and the switch for it — the part's NAME, like a mesh's. Given, the card
  // and the meshes are one rectangle, the name is the box's switch rather than what opens the
  // numbers (a click on the part still does that), and the ruler and the tag only switch the
  // words on the box. Withheld — the assembly phase — the card is what it always was.
  boxesOff?: string[];
  onToggleBox?: (instanceId: string) => void;
  // THE MEASUREMENTS NO SINGLE MESH ROW OWNS — the whole object's, those two or three meshes share,
  // "Other" — a row each, whose lines are drawn while it is lit. A mesh's own are on its name.
  measureGroups?: { title: string; on: boolean }[];
  onToggleMeasureGroup?: (title: string) => void;
  // BESIDE THE VIEWPORT rather than over its top-right corner — see PartViewer's controlsAbove.
  inline?: boolean;
}) {
  const { t: tr } = useLanguage();
  // FOLDED AWAY, where the list covers the picture — every viewer that draws it, remembered for
  // the page like the draw mode: a list folded to look at a part stays folded on the next one.
  const [folded, setFolded] = useState(listFolded);
  const [shut, setShut] = useState({ components: true, hardware: true, covers: true });
  const fold = (on: boolean) => { listFolded = on; setFolded(on); };
  if (!parts.length) return null;
  // THE LIST'S OWN TOP ROW, which folds it up and out of the picture — and, folded, is all that
  // is left of it, with the count, so it still says how much is in it.
  const foldRow = (
    <button
      type="button"
      onClick={() => fold(!folded)}
      title={tr(folded ? 'notice.assembly.listShow' : 'notice.assembly.listHide')}
      aria-expanded={!folded}
      className={`sticky top-0 z-20 flex w-full items-center justify-center gap-1 bg-white/95 py-0.5 text-[11px] font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-blue-600 ${
        folded ? '' : 'border-b border-slate-200'}`}
    >
      {folded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
      {folded && <span className="tabular-nums">{parts.length}</span>}
    </button>
  );
  const unified = !!onToggleBox;
  // THE MEASUREMENTS AND THE FACE NAMES, ONE SWITCH EACH FOR EVERY PART — at the top of the list:
  // they were two icons on every card, asked the same question of each part in turn.
  const allIds = parts.map((p) => p.instanceId);
  const ruled = allIds.some((id) => !rulersOff.includes(id));
  const named = allIds.some((id) => !(facesOff ?? []).includes(id));
  const marksRow = (
    <div className="flex items-center justify-between border-b border-slate-200 bg-white/95 px-1">
      <button
        type="button"
        onClick={() => every(allIds, (id) => !rulersOff.includes(id), onToggleRuler)}
        title={tr(ruled ? 'notice.assembly.hideMeasures' : 'notice.assembly.showMeasures')}
        className="rounded p-1 transition-colors hover:bg-slate-100"
      >
        <Ruler className={`h-3 w-3 ${ruled ? 'text-blue-600' : 'text-slate-400'}`} />
      </button>
      {onToggleFaces && (
        <button
          type="button"
          onClick={() => every(allIds, (id) => !(facesOff ?? []).includes(id), onToggleFaces)}
          title={tr(named ? 'notice.assembly.hideFaces' : 'notice.assembly.showFaces')}
          className="rounded p-1 transition-colors hover:bg-slate-100"
        >
          <Tag className={`h-3 w-3 ${named ? 'text-sky-600' : 'text-slate-400'}`} />
        </button>
      )}
    </div>
  );
  // A LIST OF ROWS — the meshes, the measurement groups: a scrolling column in the viewport's corner,
  // and under the viewport as many columns as its width holds, with nothing to scroll.
  const listLayout = inline
    ? 'grid grid-cols-[repeat(auto-fill,minmax(16rem,1fr))]'
    : 'flex max-h-60 flex-col overflow-y-auto';
  // The picked part's numbers, under the whole rectangle rather than inside it.
  const openPanel = unified && parts.some((p) => p.instanceId === picked) ? panel : null;

  // THREE GROUPS, WHERE THE PICTURE HOLDS MORE THAN ONE: the components, the hardware and the
  // covers, each folded under its own header, all folded when the viewer opens. And A PIECE THE
  // PICTURE HOLDS SEVERAL OF is one row, "JCBC screw (E) ×4", whose switches act on all of them.
  // AND THE COPIES OF THE MOVING PART — four felt pads put on in one gesture (see Fitting.copies) —
  // are its own row, "×4", as several of one piece of hardware are.
  const componentRows: Row[] = [];
  for (const p of parts.filter((x) => !x.group)) {
    // Every copy on the first's row: the moving part's, and the hardware prepared on each.
    const lead = p.instanceId.startsWith('moving#') ? 'moving' : p.instanceId.startsWith('prep#') ? 'prep'
      : p.instanceId.startsWith('next#') ? 'next' : null;
    const first = lead ? componentRows.find((r) => r.ids[0] === lead) : undefined;
    if (first) first.ids.push(p.instanceId);
    else componentRows.push({ ids: [p.instanceId], label: p.label });
  }
  const merged = (group: 'hardware' | 'cover') => {
    const rows: Row[] = [];
    for (const p of parts.filter((x) => x.group === group)) {
      const same = rows.find((r) => r.label === p.label);
      if (same) same.ids.push(p.instanceId);
      else rows.push({ ids: [p.instanceId], label: p.label });
    }
    return rows;
  };
  const groups = ([
    { key: 'components', titleKey: 'notice.assembly.groupComponents', rows: componentRows },
    { key: 'hardware', titleKey: 'notice.assembly.groupHardware', rows: merged('hardware') },
    { key: 'covers', titleKey: 'notice.assembly.groupCovers', rows: merged('cover') },
  ] as const).filter((g) => g.rows.length);
  const groupHead = (key: keyof typeof shut, titleKey: string, rows: Row[]) => {
    const ids = rows.flatMap((r) => r.ids);
    const shown = ids.some((id) => !hidden.includes(id));
    return (
      <div key={key} className="relative flex items-stretch border-b border-slate-200 bg-slate-50/90">
        <div className="absolute inset-y-0 left-0 z-10 flex items-center pl-1">
          <button
            type="button"
            onClick={() => every(ids, (id) => !hidden.includes(id), onToggle)}
            title={tr(shown ? 'notice.assembly.hidePart' : 'notice.assembly.showPart')}
            className="rounded p-1 transition-colors hover:bg-slate-100"
          >
            {shown ? <Eye className="h-3 w-3 text-blue-600" /> : <EyeOff className="h-3 w-3 text-slate-400" />}
          </button>
        </div>
        <button
          type="button"
          onClick={() => setShut((was) => ({ ...was, [key]: !was[key] }))}
          aria-expanded={!shut[key]}
          className="flex flex-1 items-center justify-center gap-1 px-8 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 transition-colors hover:text-blue-600"
        >
          {shut[key] ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
          <span>{tr(titleKey)} · <span className="tabular-nums">{ids.length}</span></span>
        </button>
      </div>
    );
  };
  const card = (row: Row) => {
      const [first] = row.ids;
      const off = row.ids.every((id) => hidden.includes(id));
      const boxOn = row.ids.some((id) => !(boxesOff ?? []).includes(id));
      const open = picked === first;
      // Named only where there is something to tell apart: a placing draws one part, which
      // is the whole of the step and needs no word saying it is the one that moves.
      const role = parts.length > 1 ? ROLES[first] : undefined;
      return (
        <div key={first} className={`flex flex-col ${unified ? '' : 'border-b border-slate-200 last:border-b-0'}`}>
          {/* TWO CONTROLS, ONE RECTANGLE. Splitting the card is what lets the name do
              something other than hide the part — and the eye keeps the whole of its old
              job, so the gesture anybody has already learned still works.

              SHOWN IS THE LOUD STATE and hidden is the quiet one, which is the way round it
              has to be: what is on screen is the normal case, and a row of cards all
              shouting would say nothing about which of them is missing. */}
          {/* A ROW OF THE ONE LIST, not a card of its own: the fold, the two switches and the parts
              were four rectangles stacked over the picture, read as four menus. The open one is
              tinted, and its numbers unfold under it inside the same rectangle. */}
          <div
            className={`relative flex items-stretch text-xs transition-colors ${
              off ? 'text-slate-400' : 'text-slate-800'
            } ${!unified && open ? 'bg-blue-50' : ''}`}
          >
            {/* LIFTED OUT OF THE ROW so the name can be centred on the CARD rather than on
                what is left of it after the eye. In the flow, the eye pushed the name's box
                to the right and a centred line sat a half-eye off the middle — the kind of
                near-miss that reads as a mistake without announcing itself. Absolute here,
                and the name carries matching padding on both sides, so the text is centred
                on the rectangle and still never runs under the icon. */}
            <div className="absolute inset-y-0 left-0 z-10 flex flex-col items-center justify-center pl-1">
              <button
                type="button"
                onClick={() => every(row.ids, (id) => !hidden.includes(id), onToggle)}
                title={tr(off ? 'notice.assembly.showPart' : 'notice.assembly.hidePart')}
                className="rounded p-1 transition-colors hover:bg-slate-100"
              >
                {off
                  ? <EyeOff className="h-3 w-3" />
                  : <Eye className="h-3 w-3 text-blue-600" />}
              </button>
            </div>

            {/* A PART THAT IS NOT IN THE PICTURE CANNOT BE THE PART YOU ARE POSITIONING, so
                the name goes dead while the eye is shut — and the eye beside it is the way
                to bring it back. The panel would close under you anyway: taking a part out
                clears the selection. */}
            {/* THE WHOLE NAME, over as many lines as it takes. "Structure dossier et pieds
                arrière (A)" was cut at "pieds a…", losing the letter in brackets that tells
                it from its neighbours — and the rest was in a tooltip, which asks somebody
                to hover a card to learn which part it names. Two lines cost nothing in a
                column this tall, and `text-balance` shares them out evenly rather than
                filling one and leaving a word on the next. */}
            <button
              type="button"
              disabled={off}
              onClick={() => (onToggleBox
                ? every(row.ids, (id) => !(boxesOff ?? []).includes(id), onToggleBox)
                : onPick(open ? null : first))}
              title={onToggleBox ? tr(boxOn ? 'notice.assembly.hideBox' : 'notice.assembly.showBox') : undefined}
              className={`flex min-h-[2rem] min-w-0 flex-1 flex-col items-center justify-center text-balance break-words px-7 py-1 text-center leading-snug transition-colors enabled:hover:bg-slate-100 disabled:cursor-default ${
                onToggleBox && boxOn && !off ? 'font-semibold text-blue-600' : ''
              }`}
            >
              {/* WHICH OF THE TWO THIS ONE IS, over its name.
                  The fitting is answered in two vocabularies at once — the manual's ("JCBC
                  screw (E)") and the movement's (which part stays, which part travels) —
                  and every field in the panel below belongs to the second. A card naming
                  only the first leaves the reader to work out, from the axis and the travel,
                  which of the two rectangles they are about to type into.
                  IN THE COLOUR THAT PART'S WIREFRAME IS DRAWN IN, and in the same order the
                  scene assigns them (see WIRE_COLOURS): the word and the lines are then two
                  ways of saying one thing, and switching to wireframe needs no legend. */}
              {role && (
                <span className={`text-[9px] font-bold uppercase tracking-[0.12em] ${ROLE_TONE[role]}`}>
                  {tr(`notice.assembly.role.${role}`)}
                </span>
              )}
              <PlusLines label={row.ids.length > 1 ? countedText(row.label, row.ids.length) : row.label} />
            </button>
          </div>
          {/* GLUED, so the numbers and the name they are about are one object. The card
              above keeps its bottom border and the panel has none of its own on top, which
              leaves a rule between two halves of one rectangle rather than two boxes
              touching. */}
          {!unified && open && panel}
        </div>
      );
  };

  // TOP RIGHT AND HARD AGAINST IT. The cards used to start a row down, clear of a toolbar
  // that is no longer over the viewport — so the gap was four rems of white above a stack that
  // can grow long enough to run off the bottom of the picture. Level with the camera readout
  // on the other side, which is the height a corner control sits at here.
  return (
    // NO TALLER THAN THE PICTURE, and scrolled with the wheel where the list is longer — a whole
    // product's parts and hardware run well past the bottom of the viewport.
    <div className={inline ? 'flex w-full flex-col gap-1.5' : `absolute right-3 flex w-52 flex-col gap-1.5 ${below ? 'top-14 max-h-[calc(100%-4.25rem)]' : 'top-3 max-h-[calc(100%-1.5rem)]'}`}>
      {/* THE ONE THAT CLEARS THE PICTURE, in the corner itself and the cards under it. It is
          about the whole viewport rather than about any part in the list, so it stands above
          the list rather than in it — and in the cards' own dress, because it lives in their
          stack and a white chip among dark ones reads as something that arrived by accident. */}
      {onToggleMarks && <MarksButton on={!!marksOn} onToggle={onToggleMarks} className="self-center" small />}
      {folded && !inline && (
        <div className="overflow-hidden rounded-lg border border-slate-300 bg-white/90 shadow-sm">
          {foldRow}
        </div>
      )}
      {!(folded && !inline) && (<>
      {/* ONE RECTANGLE: the fold, the switches, the parts and their meshes are one list. */}
      <div className="flex min-h-0 flex-col">
      <div
        className={`min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain border border-slate-300 bg-white/90 text-xs shadow-sm ${
          openPanel ? 'rounded-t-lg' : 'rounded-lg'
        }`}
      >
      {!inline && foldRow}
      {marksRow}
      {groups.length > 1
        ? groups.map((g) => [groupHead(g.key, g.titleKey, g.rows), ...(shut[g.key] ? [] : g.rows.map(card))])
        : groups.flatMap((g) => g.rows).map(card)}
      {pieces && pieces.length > 1 && onTogglePiece && (
        <div
          className={`${listLayout} border-t border-slate-300 py-0.5 text-xs`}
        >
          {(pieceOrder ? [...pieces].sort((a, b) => rankIn(pieceOrder, a) - rankIn(pieceOrder, b)) : pieces).map((name) => {
            const off = (piecesOff ?? []).includes(name);
            const boxed = (piecesBoxed ?? []).includes(name);
            return (
              <div
                key={name}
                className={`flex items-stretch ${
                  off ? 'text-slate-400' : 'text-slate-800'
                }`}
              >
                <button
                  type="button"
                  onClick={() => onTogglePiece(name)}
                  title={tr(off ? 'notice.assembly.showPart' : 'notice.assembly.hidePart')}
                  className="shrink-0 px-2 py-1 transition-colors hover:bg-slate-100"
                >
                  {off
                    ? <EyeOff className="h-3.5 w-3.5" />
                    : <Eye className="h-3.5 w-3.5 text-blue-600" />}
                </button>
                {/* THE BOX, on the name — lit while it is drawn. Not for a hidden mesh: there is
                    nothing on screen for a box to go round. */}
                <button
                  type="button"
                  disabled={off || !onBoxPiece}
                  onClick={() => onBoxPiece?.(name)}
                  title={tr(boxed ? 'notice.assembly.hideBox' : 'notice.assembly.showBox')}
                  // CENTRED IN THE ROW, the eye's width kept on the right too so the name is centred
                  // on the card and not on what the eye leaves of it.
                  className={`min-w-0 flex-1 break-words py-1 pr-[1.875rem] text-center transition-colors enabled:hover:bg-slate-100 disabled:cursor-default ${
                    boxed && !off ? 'font-semibold text-blue-600' : ''
                  }`}
                >
                  {pieceLabel ? pieceLabel(name) : name}
                </button>
              </div>
            );
          })}
        </div>
      )}
      {measureGroups && measureGroups.length > 0 && onToggleMeasureGroup && (
        <div
          className={`${listLayout} border-t border-slate-300 py-0.5 text-xs`}
        >
          {measureGroups.map(({ title, on }) => (
            <button
              key={title}
              type="button"
              onClick={() => onToggleMeasureGroup(title)}
              title={tr(on ? 'notice.assembly.hideMeasures' : 'notice.assembly.showMeasures')}
              className={`flex items-start gap-2 px-2 py-1 text-left transition-colors hover:bg-slate-100 ${
                on ? 'font-semibold text-rose-600' : 'text-slate-800'
              }`}
            >
              <Ruler className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span className="min-w-0 break-words">{title}</span>
            </button>
          ))}
        </div>
      )}
      </div>
      {openPanel}
      </div>
      </>)}
    </div>
  );
}

// ONE ROW: a part, or every piece of one piece of hardware the picture holds.
type Row = { ids: string[]; label: string };

// ONE SWITCH FOR SEVERAL: any of them on turns those off, none on turns them all on.
function every(ids: string[], isOn: (id: string) => boolean, flip: (id: string) => void) {
  const on = ids.some(isOn);
  ids.filter((id) => isOn(id) === on).forEach(flip);
}

// Whether the last list was folded — see `folded`. Open until somebody folds one (Louis 2026-10-28).
let listFolded = false;
